import { supabase } from '@/lib/supabase/client'
import {
  STMIDBEquipmentService,
  STMIDB_WLL_COMPANY_ID,
  STMIDB_WLL_COMPANY_NAME,
  mapSTMIDBInspectionStatus,
  mapSTMIDBToWLLCategoryCode,
} from '@/services/api/stmidbEquipmentService'
import { isSlingCategory } from '@/utils/categoryFamily'

export type JobStatus = 'planned' | 'in_progress' | 'completed' | 'cancelled'
export type JobType = 'periodic' | '48_months' | 'ndt' | 'service_other'
export type JobWorkType = 'annual' | '48_months' | 'ndt' | 'service'
export type JobAssignmentStatus = 'assigned' | 'completed'
/** Mirrors the `jobs_ndt_subtypes_check` constraint. */
export const NDT_SUBTYPES = ['ET', 'MPI', 'PT', 'VT', 'LT', 'UT', 'RT', 'Thread'] as const
export type NdtSubtype = (typeof NDT_SUBTYPES)[number]

/** Values of the `inspection_status` enum, in the order inspectors pick them. */
export const INSPECTION_STATUSES = ['OK', 'C', 'MO', 'RC', 'NC', 'EXP', 'Missing'] as const
export type InspectionStatus = (typeof INSPECTION_STATUSES)[number]

/** Statuses that need a remark, which is printed on the control card. */
export const REMARK_STATUSES: InspectionStatus[] = ['C', 'MO', 'RC', 'NC']
/** Statuses a control card can be published with (same as the inspection form). */
export const CONTROL_CARD_STATUSES: InspectionStatus[] = ['OK', ...REMARK_STATUSES]
/** Work types that can be published in bulk from the job list. */
export const BULK_CONTROL_CARD_WORK_TYPES: JobWorkType[] = ['annual', '48_months']

export interface JobSubEquipment {
  id: string
  serial_number: string
  name: string
  wll: string | null
  is_sling: boolean
  last_ndt_date: string | null
  next_ndt_date: string | null
}

/** Same shape the dashboard search passes to `EquipmentDetailsModal`. */
export interface JobEquipment {
  sub_equipment: JobSubEquipment[]
  category_code: string | null
  source: 'wll' | 'stmidb'
  inspection_status: string | null
  id: string
  asset_number: string
  serial_number: string
  part_number: string | null
  owner_id: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  next_inspection_date: string | null
  name: string
  company_id: string
  company_name: string
}

export interface JobAssignment {
  id: string
  scheduled_date: string | null
  work_type: JobWorkType
  comment: string | null
  status: JobAssignmentStatus
  inspection_status: InspectionStatus | null
  inspection_remarks: string | null
  /** Storage paths in the job-images bucket, attached to the control card. */
  inspection_images: string[]
  control_card_at: string | null
  equipment: JobEquipment | null
}

export interface Job {
  id: string
  job_type: JobType
  ndt_subtypes: NdtSubtype[]
  /** Extent per NDT method, e.g. `{ MPI: '100%' }`. */
  ndt_extents: Partial<Record<NdtSubtype, string>>
  description: string | null
  order_number: string | null
  location: string | null
  due_date: string | null
  status: JobStatus
  created_at: string
  inspector_id: string | null
  company_id: string | null
  company_name: string | null
  inspector_name: string | null
  /** Whoever created the job signs the control cards as ordreansvarlig. */
  creator: { id: string; full_name: string; signature: string | null } | null
  assignments: JobAssignment[]
  /** Reports published from the job, e.g. TR technical reports. */
  reports: JobReport[]
}

export interface JobReport {
  id: string
  order_number: string
  file_path: string
  created_at: string
}

/** Assignment work types each kind of saved document counts as done. */
export const NDT_REPORT_WORK_TYPES: JobWorkType[] = ['ndt', '48_months']
export const SERVICE_REPORT_WORK_TYPES: JobWorkType[] = ['service']

const OPEN_JOB_STATUSES: JobStatus[] = ['planned', 'in_progress']
const MY_JOBS_LIMIT = 50
const ALL_JOBS_LIMIT = 10

const JOBS_SELECT = `
  id,
  job_type,
  ndt_subtypes,
  ndt_extents,
  description,
  order_number,
  location,
  due_date,
  status,
  created_at,
  inspector_id,
  company_id,
  companies(name),
  inspector:profiles!jobs_inspector_id_fkey(full_name, email),
  creator:profiles!jobs_created_by_fkey(id, full_name, signature),
  reports:ndt_reports(id, order_number, file_path, created_at),
  job_assignments(
    id,
    equipment_id,
    stmidb_equipment_id,
    scheduled_date,
    work_type,
    comment,
    status,
    inspection_status,
    inspection_remarks,
    inspection_images,
    control_card_at,
    equipment:equipment_v2(
      id, name, serial_number, asset_number, part_number, owner_id,
      last_ndt_date, next_ndt_date, next_inspection_date, company_id, inspection_status,
      companies(name), categories_v2(code)
    )
  )
`

interface WllEquipmentRow {
  id: string
  name: string | null
  serial_number: string | null
  asset_number: string | null
  part_number: string | null
  owner_id: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
  next_inspection_date: string | null
  company_id: string
  inspection_status: string | null
  companies: { name: string } | null
  categories_v2: { code: string } | null
}

interface StmidbEquipmentRow {
  id: string
  serial_number: string | null
  asset_number: string | null
  part_number: string | null
  equipment_code: string | null
  description: string | null
  category: string | null
  equipment_type: string | null
  status: string | null
  next_inspection_date: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
}

interface JobRow {
  id: string
  job_type: JobType
  ndt_subtypes: NdtSubtype[] | null
  ndt_extents: Partial<Record<NdtSubtype, string>> | null
  description: string | null
  order_number: string | null
  location: string | null
  due_date: string | null
  status: JobStatus
  created_at: string
  inspector_id: string | null
  company_id: string | null
  companies: { name: string } | null
  inspector: { full_name: string | null; email: string | null } | null
  creator: { id: string; full_name: string | null; signature: string | null } | null
  reports: JobReport[] | null
  job_assignments: {
    id: string
    equipment_id: string | null
    stmidb_equipment_id: string | null
    scheduled_date: string | null
    work_type: JobWorkType
    comment: string | null
    status: JobAssignmentStatus
    inspection_status: InspectionStatus | null
    inspection_remarks: string | null
    inspection_images: string[] | null
    control_card_at: string | null
    equipment: WllEquipmentRow | null
  }[]
}

const mapWllEquipment = (row: WllEquipmentRow, subEquipment: JobSubEquipment[]): JobEquipment => ({
  sub_equipment: subEquipment,
  category_code: row.categories_v2?.code ?? null,
  source: 'wll',
  inspection_status: row.inspection_status,
  id: row.id,
  asset_number: row.asset_number || '',
  serial_number: row.serial_number || '',
  part_number: row.part_number,
  owner_id: row.owner_id,
  last_ndt_date: row.last_ndt_date,
  next_ndt_date: row.next_ndt_date,
  next_inspection_date: row.next_inspection_date,
  name: row.name || '',
  company_id: row.company_id,
  company_name: row.companies?.name ?? 'Ukjent selskap',
})

// STMIDB stores "missing" as M and approval as "PK OK"; WLL uses the enum value.
const fromStmidbStatus = (status: string | null) => {
  const mapped = mapSTMIDBInspectionStatus(status)
  return mapped === 'M' ? 'Missing' : mapped
}
const toStmidbStatus = (status: InspectionStatus) => (status === 'Missing' ? 'M' : status)

const mapStmidbEquipment = (row: StmidbEquipmentRow, subEquipment: JobSubEquipment[]): JobEquipment => ({
  sub_equipment: subEquipment,
  category_code: mapSTMIDBToWLLCategoryCode(row.category, row.equipment_type),
  source: 'stmidb',
  inspection_status: fromStmidbStatus(row.status),
  id: row.id,
  asset_number: row.asset_number || '',
  serial_number: row.serial_number || '',
  part_number: row.part_number,
  owner_id: row.equipment_code || row.asset_number,
  last_ndt_date: row.last_ndt_date,
  next_ndt_date: row.next_ndt_date,
  next_inspection_date: row.next_inspection_date,
  name: row.description || row.asset_number || '',
  company_id: STMIDB_WLL_COMPANY_ID,
  company_name: STMIDB_WLL_COMPANY_NAME,
})

// Keeps `.in()` filters well under PostgREST's URL length limit.
const ID_CHUNK_SIZE = 100

const chunk = <T,>(items: T[]): T[][] => {
  const chunks: T[][] = []
  for (let i = 0; i < items.length; i += ID_CHUNK_SIZE) chunks.push(items.slice(i, i + ID_CHUNK_SIZE))
  return chunks
}

interface RelationshipRow {
  main_equipment_id: string
  sub_equipment: {
    id: string
    name: string | null
    serial_number: string | null
    asset_number: string | null
    last_ndt_date: string | null
    next_ndt_date: string | null
    categories_v2: { code: string } | null
  } | null
}

interface WllSpecRow {
  equipment_id: string
  spec_key: string
  value_numeric: number | null
  value_integer: number | null
  value_text: string | null
  value_date: string | null
  unit: { symbol: string | null } | null
}

const WLL_SPEC_KEYS = ['wll', 'swl']
const NDT_SPEC_KEYS = ['last_ndt_date', 'ndt_date']

type WllValue = Pick<WllSpecRow, 'spec_key' | 'value_numeric' | 'value_integer' | 'value_text'>

/** `wll` wins over `swl` when an item has both. */
const addWll = (wllById: Map<string, string>, equipmentId: string, spec: WllValue, unit: string | null | undefined) => {
  if (!WLL_SPEC_KEYS.includes(spec.spec_key)) return
  const value = spec.value_numeric ?? spec.value_integer ?? spec.value_text
  if (value == null || value === '' || (wllById.has(equipmentId) && spec.spec_key === 'swl')) return
  wllById.set(equipmentId, `${value} ${unit ?? ''}`.trim())
}

interface StmidbSubEquipmentRow {
  id: string
  serial_number: string | null
  asset_number: string | null
  description: string | null
  category: string | null
  equipment_type: string | null
  last_ndt_date: string | null
  next_ndt_date: string | null
}

interface ExternalWllSpecRow extends WllValue {
  external_equipment_id: string
  unit_id: string | null
}

/** SLB sub-equipment lives in STMIDB (parent_equipment_id) and its specs in external_equipment_specifications. */
async function fetchStmidbSubEquipment(mainIds: string[]): Promise<Map<string, JobSubEquipment[]>> {
  const byMain = new Map<string, JobSubEquipment[]>()
  if (mainIds.length === 0) return byMain

  const childrenByMain = await Promise.all(
    mainIds.map(async (mainId) => {
      const { data, error } = await supabase.rpc('get_stmidb_sub_equipment', { p_parent_id: mainId })
      if (error) throw error
      return [mainId, (data ?? []) as StmidbSubEquipmentRow[]] as const
    }),
  )

  const subIds = Array.from(new Set(childrenByMain.flatMap(([, children]) => children.map((c) => c.id))))
  const specs: ExternalWllSpecRow[] = []
  for (const ids of chunk(subIds)) {
    const { data, error } = await supabase
      .from('external_equipment_specifications')
      .select('external_equipment_id, spec_key, value_numeric, value_integer, value_text, unit_id')
      .eq('source', 'stmidb')
      .in('external_equipment_id', ids)
      .in('spec_key', ['wll', 'swl'])
    if (error) throw error
    specs.push(...((data ?? []) as ExternalWllSpecRow[]))
  }

  const unitIds = Array.from(new Set(specs.flatMap((s) => s.unit_id ?? [])))
  const unitSymbols = new Map<string, string>()
  if (unitIds.length > 0) {
    const { data, error } = await supabase.from('units_v2').select('id, symbol').in('id', unitIds)
    if (error) throw error
    for (const unit of data ?? []) unitSymbols.set(unit.id, unit.symbol)
  }

  const wllById = new Map<string, string>()
  for (const spec of specs) {
    addWll(wllById, spec.external_equipment_id, spec, spec.unit_id ? unitSymbols.get(spec.unit_id) : null)
  }

  for (const [mainId, children] of childrenByMain) {
    if (children.length === 0) continue
    byMain.set(
      mainId,
      children.map((child) => ({
        id: child.id,
        serial_number: child.serial_number || child.asset_number || '',
        name: child.description || child.equipment_type || '',
        wll: wllById.get(child.id) ?? null,
        is_sling: isSlingCategory(mapSTMIDBToWLLCategoryCode(child.category, child.equipment_type)),
        last_ndt_date: child.last_ndt_date,
        next_ndt_date: child.next_ndt_date,
      })),
    )
  }
  return byMain
}

async function fetchSubEquipment(mainIds: string[]): Promise<Map<string, JobSubEquipment[]>> {
  const byMain = new Map<string, JobSubEquipment[]>()
  if (mainIds.length === 0) return byMain

  const relationships: RelationshipRow[] = []
  for (const ids of chunk(mainIds)) {
    const { data, error } = await supabase
      .from('equipment_relationship_v2')
      .select(`
        main_equipment_id,
        sub_equipment:equipment_v2!equipment_relationship_v2_sub_equipment_id_fkey(
          id, name, serial_number, asset_number, last_ndt_date, next_ndt_date, categories_v2(code)
        )
      `)
      .in('main_equipment_id', ids)
    if (error) throw error
    relationships.push(...((data ?? []) as unknown as RelationshipRow[]))
  }

  const subIds = Array.from(new Set(relationships.flatMap((rel) => rel.sub_equipment?.id ?? [])))
  const wllById = new Map<string, string>()
  const ndtSpecById = new Map<string, string>()
  for (const ids of chunk(subIds)) {
    const { data, error } = await supabase
      .from('equipment_specifications_v2')
      .select(`
        equipment_id, spec_key, value_numeric, value_integer, value_text, value_date,
        unit:units_v2!equipment_specifications_v2_unit_id_fkey(symbol)
      `)
      .in('equipment_id', ids)
      .in('spec_key', [...WLL_SPEC_KEYS, ...NDT_SPEC_KEYS])
    if (error) throw error
    for (const spec of (data ?? []) as unknown as WllSpecRow[]) {
      addWll(wllById, spec.equipment_id, spec, spec.unit?.symbol)
      const ndtDate = NDT_SPEC_KEYS.includes(spec.spec_key) ? spec.value_date || spec.value_text : null
      if (ndtDate && !ndtSpecById.has(spec.equipment_id)) ndtSpecById.set(spec.equipment_id, ndtDate)
    }
  }

  for (const rel of relationships) {
    const sub = rel.sub_equipment
    if (!sub) continue
    const list = byMain.get(rel.main_equipment_id) ?? []
    list.push({
      id: sub.id,
      serial_number: sub.serial_number || sub.asset_number || '',
      name: sub.name || '',
      wll: wllById.get(sub.id) ?? null,
      is_sling: isSlingCategory(sub.categories_v2?.code),
      last_ndt_date: sub.last_ndt_date || ndtSpecById.get(sub.id) || null,
      next_ndt_date: sub.next_ndt_date,
    })
    byMain.set(rel.main_equipment_id, list)
  }
  return byMain
}

// Pending work first, then by planned date, as in the desktop job panel.
const compareAssignments = (a: JobAssignment, b: JobAssignment) =>
  (a.status === 'completed' ? 1 : 0) - (b.status === 'completed' ? 1 : 0) ||
  (a.scheduled_date ?? a.equipment?.next_inspection_date ?? '9999').localeCompare(
    b.scheduled_date ?? b.equipment?.next_inspection_date ?? '9999',
  )

/**
 * The job closes itself once every assignment has a status, so an inspector's
 * jobs that still have control cards to publish are kept in "Mine jobber".
 */
async function fetchJobIdsWithPendingCards(inspectorId: string): Promise<string[]> {
  const { data, error } = await supabase
    .from('job_assignments')
    .select('job_id, jobs!inner(inspector_id, status)')
    .in('inspection_status', CONTROL_CARD_STATUSES)
    .in('work_type', BULK_CONTROL_CARD_WORK_TYPES)
    .is('control_card_at', null)
    .eq('jobs.inspector_id', inspectorId)
    .eq('jobs.status', 'completed')
  if (error) throw error
  return Array.from(new Set((data ?? []).map((row) => row.job_id as string)))
}

export async function fetchOpenJobs(scope: { inspectorId: string } | 'all'): Promise<Job[]> {
  const pendingCardJobIds = scope === 'all' ? [] : await fetchJobIdsWithPendingCards(scope.inspectorId)

  let query = supabase
    .from('jobs')
    .select(JOBS_SELECT)
    .order('created_at', { ascending: false })

  const openFilter = `status.in.(${OPEN_JOB_STATUSES.join(',')})`
  query = pendingCardJobIds.length > 0
    ? query.or(`${openFilter},id.in.(${pendingCardJobIds.join(',')})`)
    : query.in('status', OPEN_JOB_STATUSES)

  query = scope === 'all'
    ? query.not('inspector_id', 'is', null).limit(ALL_JOBS_LIMIT)
    : query.eq('inspector_id', scope.inspectorId).limit(MY_JOBS_LIMIT)

  const { data, error } = await query
  if (error) throw error
  const rows = (data ?? []) as unknown as JobRow[]

  const stmidbIds = Array.from(
    new Set(rows.flatMap((job) => job.job_assignments.flatMap((a) => a.stmidb_equipment_id ?? []))),
  )
  const wllIds = Array.from(
    new Set(rows.flatMap((job) => job.job_assignments.flatMap((a) => a.equipment?.id ?? []))),
  )

  const [stmidbRows, subEquipmentByMain, stmidbSubEquipmentByMain] = await Promise.all([
    stmidbIds.length > 0
      ? supabase.rpc('get_stmidb_job_equipment', { p_ids: stmidbIds }).then(({ data: result, error: rpcError }) => {
          if (rpcError) throw rpcError
          return (result ?? []) as StmidbEquipmentRow[]
        })
      : Promise.resolve([]),
    fetchSubEquipment(wllIds),
    fetchStmidbSubEquipment(stmidbIds),
  ])

  const stmidbById = new Map<string, JobEquipment>()
  for (const row of stmidbRows) {
    stmidbById.set(row.id, mapStmidbEquipment(row, stmidbSubEquipmentByMain.get(row.id) ?? []))
  }

  return rows.map((job) => ({
    id: job.id,
    job_type: job.job_type,
    ndt_subtypes: job.ndt_subtypes ?? [],
    ndt_extents: job.ndt_extents ?? {},
    description: job.description?.trim() || null,
    order_number: job.order_number,
    location: job.location,
    due_date: job.due_date,
    status: job.status,
    created_at: job.created_at,
    inspector_id: job.inspector_id,
    company_id: job.company_id,
    company_name: job.companies?.name ?? null,
    inspector_name: job.inspector?.full_name?.trim() || job.inspector?.email || null,
    creator: job.creator?.full_name?.trim()
      ? { id: job.creator.id, full_name: job.creator.full_name.trim(), signature: job.creator.signature }
      : null,
    reports: [...(job.reports ?? [])].sort((a, b) => a.order_number.localeCompare(b.order_number)),
    assignments: job.job_assignments
      .map((a) => ({
        id: a.id,
        scheduled_date: a.scheduled_date,
        work_type: a.work_type,
        comment: a.comment,
        status: a.status,
        inspection_status: a.inspection_status,
        inspection_remarks: a.inspection_remarks,
        inspection_images: a.inspection_images ?? [],
        control_card_at: a.control_card_at,
        equipment: a.stmidb_equipment_id
          ? stmidbById.get(a.stmidb_equipment_id) ?? null
          : a.equipment && mapWllEquipment(a.equipment, subEquipmentByMain.get(a.equipment.id) ?? []),
      }))
      .sort(compareAssignments),
  }))
}

export async function setJobAssignmentCompleted(assignmentId: string, completed: boolean) {
  const { error } = await supabase.rpc('set_job_assignment_completed', {
    p_assignment_id: assignmentId,
    p_completed: completed,
  })
  if (error) throw error
}

async function setJobAssignmentResult(
  assignmentId: string,
  status: InspectionStatus | null,
  remarks: string | null,
  controlCard: boolean,
  images: string[] | null = null,
) {
  const { error } = await supabase.rpc('set_job_assignment_result', {
    p_assignment_id: assignmentId,
    p_status: status,
    p_remarks: remarks,
    p_control_card: controlCard,
    p_images: images,
  })
  if (error) throw error
}

export async function updateEquipmentInspectionStatus(
  equipment: Pick<JobEquipment, 'id' | 'source'>,
  status: InspectionStatus,
  dates?: { last_inspection_date: string; next_inspection_date: string; order_no?: string },
) {
  if (equipment.source === 'stmidb') {
    const { order_no: _orderNo, ...stmidbDates } = dates ?? {}
    await STMIDBEquipmentService.updateEquipment(equipment.id, { ...stmidbDates, status: toStmidbStatus(status) })
    return
  }
  const { error } = await supabase
    .from('equipment_v2')
    .update({ ...dates, inspection_status: status })
    .eq('id', equipment.id)
  if (error) throw error
}

/**
 * Sets the equipment's inspection status and records it (with the remark and
 * images) on the assignment as done, card pending. `status` null reopens the
 * assignment; the equipment keeps its status.
 */
export async function setJobAssignmentStatus(
  assignmentId: string,
  equipment: Pick<JobEquipment, 'id' | 'source'>,
  status: InspectionStatus | null,
  remarks: string | null = null,
  images: string[] = [],
) {
  if (status) await updateEquipmentInspectionStatus(equipment, status)
  await setJobAssignmentResult(assignmentId, status, remarks, false, images)
}

/** Marks the assignment's result as published on a control card. */
export async function markJobAssignmentCardPublished(
  assignmentId: string,
  status: InspectionStatus,
  remarks: string | null,
) {
  await setJobAssignmentResult(assignmentId, status, remarks, true)
}

/**
 * Ticks off the current inspector's open assignments for this equipment after a
 * control card or report has been saved. Never throws: saving the document must
 * not fail because of job bookkeeping.
 */
export async function completeJobAssignmentsForEquipment(
  equipmentId: string,
  workTypes: JobWorkType[],
  controlCard?: { status: string; remarks: string | null },
) {
  try {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return

    let query = supabase
      .from('job_assignments')
      .select('id, jobs!inner(inspector_id, status)')
      .or(`equipment_id.eq.${equipmentId},stmidb_equipment_id.eq.${equipmentId}`)
      .in('work_type', workTypes)
      .eq('jobs.inspector_id', user.id)
    // A status picked in the job list leaves the job open for its card, even once completed.
    query = controlCard
      ? query.is('control_card_at', null).in('jobs.status', [...OPEN_JOB_STATUSES, 'completed'])
      : query.eq('status', 'assigned').in('jobs.status', OPEN_JOB_STATUSES)
    const { data, error } = await query
    if (error) throw error

    await Promise.all(
      (data ?? []).map((row) =>
        controlCard
          ? markJobAssignmentCardPublished(row.id, controlCard.status as InspectionStatus, controlCard.remarks)
          : setJobAssignmentCompleted(row.id, true),
      ),
    )
  } catch (err) {
    console.error('Could not mark job assignment as completed:', err)
  }
}
